import { FC, SyntheticEvent, useState } from 'react'
import { OptionTemplate, Tooltip } from '@cargomart/ui'
import { useNavigate } from '@tanstack/react-router'
import { handleFetchResponse } from 'api/errors'
import IconDetach from 'assets/detach.svg?react'
import { MODAL_IDS, ModalItem, ModalSimple, useModal } from 'components'
import { prepareContactUpdateData, useCreateContactMutation, useLazyGetContactListQuery } from 'modules/contact/api'
import { ContactCreateForm } from 'modules/contact/components'
import { getContactFullName, getContactFullPhone } from 'modules/contact/helpers'
import { contactDetailRoute } from 'modules/contact/routes'
import { TContact } from 'modules/contact/types'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'
import { IconTooltipHelp } from 'src/tailwind-components/shared/components/IconTooltip'

import AttachBlock from '../AttachBlock/AttachBlock'
import { CustomerContactsTableProps } from './types'

const CustomerContactsTable: FC<CustomerContactsTableProps> = (props) => {
  const {
    customerId,
    isAccessAddContact,
    isAccessDeleteContact,
    attachedContacts,
    isAttachedContactsFetching,
    contacts,
    refetchAttachedContacts,
    isContactListLoading,
    isContactListFetching,
    refetchContacts,
    attachContactByCustomerId,
    detachContactByCustomerId,
    searchQuery,
    handleSearchContact,
    setIsAttachBlockFullHeight
  } = props
  const navigate = useNavigate()
  const { closeModal, openModal } = useModal()
  const [isShowAttachBtn, setIsShowAttachBtn] = useState(true)
  const [createContact, { isLoading: isCreating }] = useCreateContactMutation()

  const toolTipText = 'Контакт может выступать представителем одного или нескольких юридических лиц.'
  const noContactsText = 'Нет связанных контактов'

  const handleOpenModalDetail = (contact: TContact) => {
    navigate({ to: contactDetailRoute.to, params: { id: contact.id } })
  }

  const handleAttach = (customerId: string, contactId: string) => {
    attachContactByCustomerId &&
      // @ts-ignore
      attachContactByCustomerId({ customerId, id: contactId })
        .unwrap()
        .then(() => {
          handleSearchContact('')
          setIsShowAttachBtn(true)
          refetchAttachedContacts && refetchAttachedContacts()
          setIsAttachBlockFullHeight && setIsAttachBlockFullHeight(false)
        })
  }

  const handleOpenContactAddModal = () => {
    setIsShowAttachBtn(true)
    openModal(MODAL_IDS.OrderCreateAddContactList)
  }

  const handleCloseContactAddModal = () => {
    closeModal(MODAL_IDS.OrderCreateAddContactList)
  }

  const handleDetach = (e: SyntheticEvent, customerId: string, contactId: string) => {
    e.stopPropagation()
    detachContactByCustomerId &&
      // @ts-ignore
      detachContactByCustomerId({ customerId, id: contactId })
        .unwrap()
        .then(() => {
          refetchAttachedContacts && refetchAttachedContacts()
        })
  }

  const [getSearchContact] = useLazyGetContactListQuery()

  const handleCreateContact = (contact: TContact) =>
    createContact(prepareContactUpdateData(contact)).then(
      handleFetchResponse((response) => {
        handleCloseContactAddModal()
        // TODO перенести на теги
        refetchContacts && refetchContacts()
        handleAttach(customerId, response.data.contact.id)
        getSearchContact('')
      })
    )

  return (
    <>
      <div className="mt-4 rounded-lg border border-solid border-[#c7c6ca]">
        <div className="flex justify-between border-b border-solid border-b-[#e0e2ec] p-4">
          <h2 className="text-title-md text-[#1a1c1e]">Контакты</h2>
          <IconTooltipHelp placement="right">{toolTipText}</IconTooltipHelp>
        </div>
        <div
          className="padding-[10px,4px] border-solidContactCreateForm flex max-h-[320px] flex-col overflow-y-auto border-b border-b-[#e0e2ec] max-md:max-h-[250px]"
          data-test-id="customers-page--customer-detail--attached-contact-list"
        >
          {/* eslint-disable-next-line */}
          {attachedContacts?.length ? (
            attachedContacts?.map((attachedContact) =>
              isAttachedContactsFetching ? (
                <PlaceholderCell className="mb-[10px] px-4 py-0" height={52} />
              ) : (
                <div key={attachedContact.id} data-test-id="contact-item">
                  <OptionTemplate
                    className="items-center pl-4"
                    description={getContactFullPhone(attachedContact)}
                    onClick={() => handleOpenModalDetail(attachedContact)}
                    title={getContactFullName(attachedContact)}
                    postfix={
                      isAccessDeleteContact ? (
                        <div data-test-id="contact-detach-btn">
                          <Tooltip content="Открепить от организации" placement="right">
                            <div>
                              <IconDetach
                                containerSize="lg"
                                onClick={(e: SyntheticEvent) => handleDetach(e, customerId, attachedContact.id)}
                                size="xs"
                              />
                            </div>
                          </Tooltip>
                        </div>
                      ) : null
                    }
                  />
                </div>
              )
            )
          ) : (
            <div
              className="p-4 text-title-md text-[#6e7076]"
              data-test-id="customers-page--customer-detail--empty-attached-contact-list"
            >
              {noContactsText}
            </div>
          )}
        </div>
        <AttachBlock
          contacts={contacts}
          customerId={customerId}
          handleAttach={handleAttach}
          handleOpenContactAddModal={handleOpenContactAddModal}
          handleSearchContact={handleSearchContact}
          isAccessAddContact={isAccessAddContact}
          isContactListFetching={isContactListFetching}
          isContactListLoading={isContactListLoading}
          isShowAttachBtn={isShowAttachBtn}
          refetchContacts={refetchContacts}
          searchQuery={searchQuery}
          setIsShowAttachBtn={setIsShowAttachBtn}
        />
      </div>
      <ModalItem id={MODAL_IDS.OrderCreateAddContactList}>
        <ModalSimple
          id={MODAL_IDS.OrderCreateAddContactList}
          onClose={handleCloseContactAddModal}
          size="nm"
          title="Добавление контакта"
        >
          <ContactCreateForm
            createContact={handleCreateContact}
            isCreating={isCreating}
            onClose={handleCloseContactAddModal}
          />
        </ModalSimple>
      </ModalItem>
    </>
  )
}

export default CustomerContactsTable
